import { ReebeloConditions } from '@lambda/reebelo';
import { Crown, LucideIcon, RotateCcw, ShieldCheck } from 'lucide-react';
import { AdditionalInfoT } from '@/components/v2/product/hooks/types';
import { formatOrigin } from '@/components/v2/product/hooks/helpers';
import { formatWarrantyInfo } from '@/lib/warranty';
import settings from '@/settings';
import i18n from '@/settings/i18n';

const t = i18n.init('product');

export type BenefitItemT = {
  id: string;
  label: string;
  flag?: ReturnType<typeof formatOrigin>['flag'];
  icon: LucideIcon;
};

const BATTERY_GUARANTEE_CATEGORIES = [
  'Smartphones',
  'Laptops',
  'Smartwatches',
  'Tablets',
];

const matchesAnyCategory = ({
  breadcrumb,
  categoryName,
  categories,
}: {
  breadcrumb: AdditionalInfoT['breadcrumb'];
  categoryName?: string;
  categories: readonly string[];
}) =>
  (categoryName ? categories.includes(categoryName) : false) ||
  (breadcrumb ?? []).some((b) => categories.includes(b.name));

// Translates the warranty duration's unit (e.g. "12 Months" -> "12 mois")
// while keeping the English display unchanged ("12-Month").
export const localizeWarrantyDuration = (
  warranty: string,
  normalized: string,
) => {
  if (i18n.getLocale() === 'en') {
    return normalized;
  }

  const match = warranty.match(/^(up to\s+)?(\d+)\s+(month|year|day)s?$/i);

  if (!match) {
    return normalized;
  }

  const [, upTo, count, unitRaw] = match;
  const isPlural = Number(count) > 1;
  const unit = unitRaw.toLowerCase();

  const unitLabel = (() => {
    if (unit === 'year') {
      return isPlural ? t`years` : t`year`;
    }

    if (unit === 'day') {
      return isPlural ? t`days` : t`day`;
    }

    return isPlural ? t`months` : t`month`;
  })();

  const duration = `${count} ${unitLabel}`;

  return upTo ? `${t`Up to`} ${duration}` : duration;
};

const formatWarrantyLabel = (warranty: string) => {
  if (!warranty) {
    return t`Warranty`;
  }

  const normalizedWarranty = warranty.replace(/(\d+)\s+Months?/i, '$1-Month');

  if (normalizedWarranty.toLowerCase().includes('warranty')) {
    return t(normalizedWarranty);
  }

  return t`${{
    warranty: localizeWarrantyDuration(warranty, normalizedWarranty),
  }} Warranty`;
};

const getRefurbishedLabel = (
  formattedOrigin: ReturnType<typeof formatOrigin>,
  condition: string,
) => {
  if (condition === 'Brand New') {
    return {
      label: t`Brand New`,
    };
  }

  // `flag` is the local flag for local vendors, the EU flag for non-local
  // vendors in EU markets, and null otherwise (so no flag renders).
  return {
    label: t`Professionally Refurbished`,
    flag: formattedOrigin.flag,
  };
};

type ProductUspsConfigArgs = {
  additionalInfo: AdditionalInfoT;
  condition: string;
  freeReturnDays: number;
  categoryName?: string;
  vendorCountry?: string;
};

export const getProductUspsConfig = ({
  additionalInfo,
  condition,
  freeReturnDays,
  categoryName,
  vendorCountry,
}: ProductUspsConfigArgs) => {
  const breadcrumb = additionalInfo?.breadcrumb ?? [];

  const shouldShowBatteryGuarantee = matchesAnyCategory({
    breadcrumb,
    categoryName,
    categories: BATTERY_GUARANTEE_CATEGORIES,
  });

  const isRefurbishedAndSG =
    settings.store === 'quista' && condition !== ReebeloConditions.BrandNew;

  const generateWarrantyLabel = () => {
    if (condition === 'Brand New') {
      return 'Manufacturer warranty';
    }

    const formattedWarrantyInfo = formatWarrantyInfo(
      additionalInfo.categoryWarrantyInfo,
    );

    if (isRefurbishedAndSG) {
      return '3 Months';
    }

    return formattedWarrantyInfo;
  };

  const categoryWarranty = generateWarrantyLabel();

  const displayWarranty =
    categoryWarranty || `${settings.warranty_month} Months`;

  const isLocalVendor =
    vendorCountry?.toUpperCase() === settings.country_code?.toUpperCase();
  const formattedOrigin = formatOrigin(Boolean(isLocalVendor));
  const refurbishedBenefit = getRefurbishedLabel(formattedOrigin, condition);

  // Professionally Refurbished
  // Warranty + Battery
  const items: BenefitItemT[] = [
    {
      id: 'refurbished',
      label: refurbishedBenefit.label,
      flag: refurbishedBenefit.flag,
      icon: Crown,
    },
    {
      id: 'warranty',
      label: `${formatWarrantyLabel(displayWarranty)}${
        shouldShowBatteryGuarantee ? ` ${t`incl. Battery`}` : ''
      }`,
      icon: ShieldCheck,
    },
  ];

  // Risk-Free Trial
  items.push({
    id: 'returns',
    label: t`${{ freeReturnDays }}-Day Risk-Free Trial`,
    icon: RotateCcw,
  });

  return {
    categoryWarranty,
    displayWarranty,
    isEasyReturnCategory: false,
    items,
  };
};
